import { Link } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/Badge";
import { CARD_CLASS } from "@/components/ui/Card";
import { STATUS_FILL } from "@/components/ui/statusTone";
import { usePods } from "@/features/pods/usePods";
import { STATUS_LABEL, type PodStatus } from "@/features/pods/types";
import styles from "@/features/pods/PodListScreen.module.css";

/** How many sensor readings fit on a card before it starts to look like a table. */
const SENSORS_ON_CARD = 3;

/**
 * The three states worth counting in the header. Offline pods report nothing to summarise.
 *
 * The wording is the header's own and not `STATUS_LABEL`, which calls `nominal` "Normal". The two
 * disagree on this screen already and changing either is a copy decision, not a styling one.
 */
const COUNTED: { status: PodStatus; label: string }[] = [
  { status: "nominal", label: "Nominal" },
  { status: "warning", label: "Warning" },
  { status: "fault", label: "Fault" },
];

export function PodListScreen() {
  const { pods, isLoading, error } = usePods();

  return (
    <PageContainer className="gap-8 max-md:gap-6">
      <div className={styles.header}>
        <div>
          <h1 className="text-2xl font-semibold">BioPods</h1>
          <p className="mt-1 text-sm text-text-muted">
            {isLoading ? "Loading…" : `${String(pods.length)} pods monitored`}
          </p>
        </div>

        {!isLoading && error === null && (
          <div className="flex gap-6">
            {COUNTED.map(({ status, label }) => (
              <div key={status} className="flex items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_FILL[status]}`} />
                <span className="text-base font-semibold">
                  {pods.filter((pod) => pod.status === status).length}
                </span>
                <span className="text-sm text-text-muted">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error !== null && (
        <p role="alert" className="text-sm text-text-muted">
          {error}
        </p>
      )}

      {!isLoading && error === null && pods.length === 0 && (
        <p className="text-sm text-text-muted">No pods are registered to this fleet.</p>
      )}

      <div className={styles.grid}>
        {pods.map((pod) => (
          <Link
            key={pod.id}
            to={`/pods/${pod.id}`}
            className={`${CARD_CLASS} flex flex-col gap-4 p-5 text-inherit transition hover:border-neutral-300 hover:shadow-md`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="block text-lg font-semibold text-text">{pod.id}</span>
                <span className="mt-0.5 block text-xs text-text-muted">{pod.site}</span>
              </div>
              <Badge tone={pod.status}>{STATUS_LABEL[pod.status]}</Badge>
            </div>

            <div className="flex flex-wrap gap-4">
              {pod.sensors.slice(0, SENSORS_ON_CARD).map((sensor) => (
                <div key={sensor.label} className="flex flex-col gap-0.5">
                  <span className="text-xs text-text-muted">{sensor.label}</span>
                  <span className="font-mono text-sm font-medium">
                    {sensor.value}
                    {sensor.unit}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <span className="text-xs font-medium text-accent">View pod status →</span>
            </div>
          </Link>
        ))}
      </div>
    </PageContainer>
  );
}
